import { useState, type ReactNode } from 'react';
import type { ProviderKind } from '@hangar/shared';
import { toast } from 'sonner';
import { useMe, useProviderAccountMutations } from '@/api/hooks';
import type { ProviderAccount, ProviderAccountTestResult } from '@/api/schemas';
import { showMutationError } from '@/lib/mutationError';
import { useCan, useFeature, type Can } from '@/lib/permissions';
import type { RowAction } from '@/components/RowActions';
import { ProviderAccountOverlays } from './ProviderAccountOverlays';
import { isPlatformOperator, providerRowActions, statusToast, testToast } from './providerAccountModel';
import { useAccountDialogs } from './useAccountDialogs';

export interface ProviderAccountActions {
  /** Every provider-account write needs `admin`. */
  can: Can;
  /**
   * A Malible platform operator: only operators add or change provider accounts (SPEC decision 19, Malible provisions
   * each client's Expo and Bitrise accounts); client admins see them read-only, "Provided by Malible".
   */
  operator: boolean;
  /** Why "Connect" and "Replace token" are held back (this Hangar cannot store tokens yet), if so. */
  tokenBlocked: string | undefined;
  /** Opens the connect sheet on a provider (Expo by default). */
  connect: (kind?: ProviderKind) => void;
  /** Edit · Replace token · Test connection · Disable/Enable. */
  actionsFor: (account: ProviderAccount) => RowAction[];
  runTest: (account: ProviderAccount) => Promise<void>;
  setStatus: (account: ProviderAccount, status: 'active' | 'disabled') => Promise<void>;
  /** The account being tested, and the last result per account. */
  testing: string | null;
  results: Record<string, ProviderAccountTestResult>;
  /** Connect/edit sheet, replace-token dialog and the disable confirmation; render once. */
  overlays: ReactNode;
}

const TOKENS_UNAVAILABLE = 'This Hangar cannot store tokens yet';

/** "Test connection": the account being tested and the last result per account. */
function useConnectionTest() {
  const { test } = useProviderAccountMutations();
  const [testing, setTesting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ProviderAccountTestResult>>({});
  const runTest = async (a: ProviderAccount) => {
    setTesting(a.id);
    try {
      const r = await test.mutateAsync({ id: a.id });
      setResults((s) => ({ ...s, [a.id]: r }));
      const t = testToast(a.name, r);
      if (t.ok) toast.success(t.title, { description: t.description });
      else toast.error(t.title, { description: t.description });
    } catch (e) {
      showMutationError(e, 'Could not test the account');
    } finally {
      setTesting(null);
    }
  };
  return { testing, results, runTest };
}

/** Enable or disable an account (`PATCH status`); `onSaved` closes the disable confirmation. */
function useAccountStatus(onSaved: () => void) {
  const { patch } = useProviderAccountMutations();
  const setStatus = async (a: ProviderAccount, status: 'active' | 'disabled') => {
    try {
      await patch.mutateAsync({ id: a.id, body: { status }, etag: a.etag });
      const t = statusToast(a.name, status);
      toast.success(t.title, { description: t.description });
      onSaved();
    } catch (e) {
      showMutationError(e, 'Could not change the account');
    }
  };
  return { setStatus, pending: patch.isPending };
}

/** Connect, edit, re-token, test and disable provider accounts (operators only): Getting started and Integrations. */
export function useProviderAccountActions(): ProviderAccountActions {
  const can = useCan('admin');
  const operator = isPlatformOperator(useMe().data);
  const credentials = useFeature('credentials');
  const dialogs = useAccountDialogs();
  const { testing, results, runTest } = useConnectionTest();
  const status = useAccountStatus(() => dialogs.setDisabling(null));
  const tokenBlocked = credentials === false ? TOKENS_UNAVAILABLE : undefined;
  const gate = { hidden: can.hidden || !operator, reason: can.allowed ? undefined : can.reason, tokenBlocked };
  const handlers = {
    edit: (a: ProviderAccount) => dialogs.setSheet({ editing: a }),
    replaceToken: dialogs.setReplacing,
    test: (a: ProviderAccount) => void runTest(a),
    enable: (a: ProviderAccount) => void status.setStatus(a, 'active'),
    disable: dialogs.setDisabling,
  };
  const overlays = <ProviderAccountOverlays dialogs={dialogs} busy={status.pending} onDisable={(a) => void status.setStatus(a, 'disabled')} />;
  return { can, operator, tokenBlocked, connect: (kind) => dialogs.setSheet({ kind }), actionsFor: (a) => providerRowActions(a, gate, handlers), runTest, setStatus: status.setStatus, testing, results, overlays };
}
